import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import Layout from "../components/Layout";
import { Search, Users, Package, Filter, UserCog } from 'lucide-react';
import { io, Socket } from 'socket.io-client';
import { API_URL, SOCKET_URL } from "../config";


interface UserOrderRow {
  userid: number;
  name: string;
  email: string;
  role: string;
  isBlocked: boolean;
  status: string;
  createdAt: string;
  phone?: string;
  address?: string;
  orderid: number | null;
  orderno: string | null;
  orderref: string | null;
  orderstatus: string | null;  // ✅ Important!
  orderdate: string | null;
  jobtype: string | null;
  isnew: boolean;
  haschanges: boolean;
  hasunreadmessages: boolean;
}

interface Order {
  id: number;
  orderNo: string;
  orderRef: string;
  status: string;
  orderDate: string;
  userName?: string;
  jobType?: string;
  // ADAUGĂ ACESTEA:
  isNew: boolean;
  hasChanges: boolean;
  hasUnreadMessages: boolean;
}

interface User {
  userId: number;
  name: string;
  email: string;
  role: string;
  orders: Order[];
  // ADAUGĂ ACEASTA:
  hasNewActivity: boolean;
  hasUnreadMessages: boolean;
}

const AdminDashboard = () => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null);
  const [isGlobalSearch, setIsGlobalSearch] = useState(true);

  const [socket, setSocket] = useState<Socket | null>(null);



  // Stări pentru căutare globală
  const [globalSearchCriteria, setGlobalSearchCriteria] = useState({
    orderNo: "",
    orderRef: "",
    trackingNo: "",
    jobType: "",
    shippedBy: "",
    startDate: "",
    endDate: "",
    shippedDate: "",
    status: "All",
  });

  // Stări pentru căutare per user
  const [userSearchCriteria, setUserSearchCriteria] = useState({
    orderRef: "",
    trackingNo: "",
    jobType: "",
    shippedBy: "",
    startDate: "",
    endDate: "",
    shippedDate: "",
    status: "All",
  });

  const [searchResults, setSearchResults] = useState<Order[] | null>(null);
  const [isSearching, setIsSearching] = useState(false);

  const fetchAllUsersAndOrders = useCallback(async (token: string) => {
    setLoading(true);
    try {
      const res = await axios.get<UserOrderRow[]>(`${API_URL}/admin/all-users-orders`, {
  headers: { Authorization: `Bearer ${token}` }
});


      const usersMap: { [key: number]: User } = {};

      res.data.forEach((row) => {
        const uid = row.userid; // ✅ userid (nu userId!)

        // ✅ ÎNTOTDEAUNA creează utilizatorul
        if (!usersMap[uid]) {
          usersMap[uid] = {
            userId: uid,
            name: row.name,
            email: row.email,
            role: row.role,
            orders: [],
            hasNewActivity: false,
            hasUnreadMessages: false,
          };
        }

        // ✅ Adaugă comenzile DOAR dacă există
        if (row.orderid && row.orderref && row.orderstatus && row.orderdate) {
          const order = {
            id: row.orderid,
            orderNo: row.orderno || '',
            orderRef: row.orderref,
            status: row.orderstatus, // ✅ orderstatus (nu orderStatus!)
            orderDate: row.orderdate,
            jobType: row.jobtype || '',
            isNew: row.isnew || false,
            hasChanges: row.haschanges || false,
            hasUnreadMessages: row.hasunreadmessages || false,
          };

          usersMap[uid].orders.push(order);

          if (order.hasUnreadMessages) {
            usersMap[uid].hasUnreadMessages = true;
          }
        }
      });

      const usersArray = Object.values(usersMap);

      // Sortează utilizatorii după cea mai recentă comandă
      const sortedUsers = usersArray.sort((a, b) => {
        const aLatest = a.orders.length ? Math.max(...a.orders.map(order => new Date(order.orderDate).getTime())) : 0;
        const bLatest = b.orders.length ? Math.max(...b.orders.map(order => new Date(order.orderDate).getTime())) : 0;
        return bLatest - aLatest;
      });

      // Sortează comenzile pentru fiecare user în ordine descrescătoare
      sortedUsers.forEach(user => {
        user.orders.sort((a, b) => new Date(b.orderDate).getTime() - new Date(a.orderDate).getTime());
      });

      setUsers(sortedUsers);
    } catch (error) {
      console.error("Eroare la aducerea datelor:", error);
      setUsers([]);
    } finally {
      setLoading(false);
    }
  }, []);


useEffect(() => {
  const token = localStorage.getItem("token");
  if (!token) return;

  try {
    const socket = io(`${SOCKET_URL}/admin`, {
      auth: { token },
      withCredentials: true,
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionAttempts: 10,
      reconnectionDelay: 2000,
      path: "/socket.io"
    });

    socket.on('connect', () => {
      console.log('✅ Admin socket conectat:', socket.id);
    });

    socket.on('connect_error', (error) => {
      console.error('❌ Eroare conexiune socket:', error);
    });

    const handleUserNotification = (data: any) => {
      console.log('=== NOTIFICARE ADMIN PRIMITĂ ===', data);

      if (data.type === 'newMessage') {
        setUsers(prevUsers => prevUsers.map(user => {
          if (user.userId === data.userId) {
            const updatedOrders = user.orders.map(order =>
              order.id === data.orderId || order.id === Number(data.orderId)
                ? { ...order, hasUnreadMessages: true }
                : order
            );
            return { ...user, orders: updatedOrders, hasUnreadMessages: true };
          }
          return user;
        }));

        setSearchResults(prevResults => prevResults?.map(order =>
          order.id === data.orderId || order.id === Number(data.orderId)
            ? { ...order, hasUnreadMessages: true }
            : order
        ) ?? null);
      }
    };

    socket.on('adminNotification', handleUserNotification);

    setSocket(socket);

    return () => {
      socket.off('adminNotification', handleUserNotification);
      socket.disconnect();
    };
  } catch (error) {
    console.error('Eroare la inițializarea socket:', error);
  }
}, []);


  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      navigate("/admin-login");
      return;
    }
    fetchAllUsersAndOrders(token);
  }, [navigate, fetchAllUsersAndOrders]);


  const handleGlobalSearchChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setGlobalSearchCriteria({ ...globalSearchCriteria, [e.target.name]: e.target.value });
  };

  const handleUserSearchChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    setUserSearchCriteria({ ...userSearchCriteria, [e.target.name]: e.target.value });
  };

  const handleGlobalSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSearching(true);
    setSearchResults(null);

    const token = localStorage.getItem("token");
    try {
      const res = await axios.get<Order[]>(`${API_URL}/admin/search-orders`, {
  headers: { Authorization: `Bearer ${token}` },
  params: globalSearchCriteria,
});

      setSearchResults(res.data);
    } catch (error) {
      console.error("Eroare la căutarea comenzilor:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const handleUserSearchSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedUserId) return;

    setIsSearching(true);
    setSearchResults(null);

    const token = localStorage.getItem("token");
    try {
      const res = await axios.get<Order[]>(`${API_URL}/admin/search-orders`, {
  headers: { Authorization: `Bearer ${token}` },
  params: { ...userSearchCriteria, userId: selectedUserId },
});

      setSearchResults(res.data);
    } catch (error) {
      console.error("Eroare la căutarea comenzilor:", error);
      setSearchResults([]);
    } finally {
      setIsSearching(false);
    }
  };

  const clearSearch = () => {
    setSearchResults(null);
    setGlobalSearchCriteria({
      orderNo: "",
      orderRef: "",
      trackingNo: "",
      jobType: "",
      shippedBy: "",
      startDate: "",
      endDate: "",
      shippedDate: "",
      status: "All",
    });
    setUserSearchCriteria({
      orderRef: "",
      trackingNo: "",
      jobType: "",
      shippedBy: "",
      startDate: "",
      endDate: "",
      shippedDate: "",
      status: "All",
    });
  };

  const markOrderAsSeen = async (orderId: number) => {
    console.log('=== MARKING ORDER AS SEEN ===', orderId);
    try {
      const token = localStorage.getItem("token");
      const response = await axios.put(`${API_URL}/admin/orders/${orderId}/mark-seen`, {}, {
  headers: { Authorization: `Bearer ${token}` }
});


      console.log('Backend response:', response.data);

      // ✅ Actualizează starea local pentru a elimina punctele galbene ȘI hasChanges
      setUsers(prevUsers => {
        const newUsers = prevUsers.map(user => ({
          ...user,
          orders: user.orders.map(order => {
            if (order.id === orderId) {
              console.log('Order BEFORE update:', order);
              const updatedOrder = {
                ...order,
                hasUnreadMessages: false,
                hasChanges: false
              };
              console.log('Order AFTER update:', updatedOrder);
              return updatedOrder;
            }
            return order;
          }),
          hasUnreadMessages: user.orders.some(order =>
            order.id !== orderId && order.hasUnreadMessages
          )
        }));

        console.log('Updated users state:', newUsers);
        return newUsers;
      });

      // ✅ Actualizează și search results
      setSearchResults(prevResults => {
        if (prevResults === null) return prevResults;

        return prevResults.map(order => {
          if (order.id === orderId) {
            return {
              ...order,
              hasUnreadMessages: false,
              hasChanges: false
            };
          }
          return order;
        });
      });

    } catch (error) {
      console.error("Error marking order as seen:", error);
    }
  };

  const hasUnreadMessages = (user: User) =>
    user.orders.some((order) =>
      order.hasUnreadMessages // DOAR pentru mesaje necitite
    );

  const hasNewOrders = (user: User) =>
    user.orders.some((order) =>
      (order.status === 'Created' && order.isNew) // DOAR comenzile Created cu isNew
    );

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="flex items-center space-x-3">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#025951]"></div>
            <span className="text-lg text-gray-700 dark:text-gray-300">Loading dashboard...</span>
          </div>
        </div>
      </Layout>
    );
  }

  const selectedUser = users.find((user) => user.userId === selectedUserId);

  let title = "Search Orders";
  if (!isGlobalSearch && selectedUser) {
    title = `Search Orders for ${selectedUser.name}`;
  } else if (searchResults !== null) {
    title = isGlobalSearch ? "Search Results" : `Search Results for ${selectedUser?.name}`;
  }

  return (
    <Layout>
      <div className="min-h-screen text-gray-900 dark:text-white transition-all duration-300">

        {/* Welcome message în dreapta sus */}
        <div className="absolute top-6 right-6">
          <span className="text-sm text-gray-600 dark:text-gray-400">Welcome Admin</span>
        </div>

        <div className="flex flex-col lg:flex-row max-w-7xl mx-auto p-4 md:p-6 gap-8 pt-16">

          {/* Sidebar cu utilizatorii */}
          <div className="w-full lg:w-80">
            <div className="bg-neutral-50 dark:bg-neutral-600 rounded-2xl shadow-lg border border-gray-200 dark:border-neutral-500 p-6 h-fit max-h-[80vh] overflow-y-auto">
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-green dark:bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                  <Users className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-xl font-semibold text-gray-900 dark:text-white uppercase tracking-wide">USERS</h2>
              </div>

              <div className="space-y-3">
                {/* Manage Clients Button */}
                <div
                  className="cursor-pointer p-3 rounded-lg border transition-all duration-200 hover:scale-[1.02] bg-neutral-50 dark:bg-neutral-600 border-gray-300 dark:border-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-500 text-gray-700 dark:text-gray-300"
                  onClick={() => navigate('/manage-clients')}
                >
                  <div className="flex items-center">
                    <UserCog className="w-4 h-4 mr-3" />
                    <span className="font-medium text-sm">Manage Clients</span>
                  </div>
                </div>

                {/* Search All Orders Button - mai subțire */}
                <div
                  className={`cursor-pointer p-2 rounded-lg border transition-all duration-200 hover:scale-[1.02] ${isGlobalSearch
                    ? "bg-green dark:bg-emerald-500 hover:bg-[#01443f] dark:hover:bg-emerald-600 text-white border-[#025951] shadow-lg"
                    : "bg-neutral-50 dark:bg-neutral-600 border-gray-300 dark:border-neutral-500 hover:bg-neutral-100 dark:hover:bg-neutral-500 text-gray-700 dark:text-gray-300"
                    }`}
                  onClick={() => {
                    setIsGlobalSearch(true);
                    setSelectedUserId(null);
                    setSearchResults(null);
                  }}
                >
                  <div className="flex items-center">
                    <Search className="w-4 h-4 mr-2" />
                    <span className="font-medium text-sm">Search All Orders</span>
                  </div>
                </div>

                {/* Lista de utilizatori - mai subțiri */}
                {users.map((user) => (
                  <div
                    key={`user-${user.userId}`}
                    className={`cursor-pointer p-2 rounded-lg transition-all duration-200 hover:scale-[1.02] border ${user.userId === selectedUserId && !isGlobalSearch
                      ? "bg-green dark:bg-emerald-500 hover:bg-[#01443f] dark:hover:bg-emerald-600 text-white border-[#025951] shadow-lg"
                      : "bg-neutral-50 dark:bg-neutral-600 hover:bg-neutral-100 dark:hover:bg-neutral-500 border-gray-200 dark:border-neutral-500 text-gray-700 dark:text-gray-300"
                      } ${hasNewOrders(user) ? "border-l-4 !border-l-red-500" : ""
                      }`}
                    onClick={() => {
                      setIsGlobalSearch(false);
                      setSelectedUserId(user.userId);
                      setSearchResults(null);
                    }}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">{user.name || user.email}</div>
                      </div>
                      <div className="flex items-center space-x-2 ml-2">
                        <span className={`text-xs ${user.userId === selectedUserId && !isGlobalSearch ? 'text-white/80' : 'text-gray-500 dark:text-gray-400'}`}>
                          {user.orders.length} orders
                        </span>

                        {/* Punctul galben pentru mesaje necitite */}
                        {hasUnreadMessages(user) && (
                          <div className="w-2 h-2 bg-yellow-500 rounded-full"></div>
                        )}

                        {/* Badge-ul roșu pentru comenzi Created */}
                        {hasNewOrders(user) && (
                          <span className="bg-red-500 text-white text-xs font-bold px-1 py-0.5 rounded-full">
                            NEW
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Main content */}
          <div className="flex-1">
            <div className="bg-neutral-50 dark:bg-neutral-600 rounded-2xl shadow-lg border border-gray-200 dark:border-neutral-500 p-8">

              {/* Header */}
              <div className="flex items-center mb-6">
                <div className="w-10 h-10 bg-green dark:bg-emerald-500 rounded-lg flex items-center justify-center mr-3">
                  <Filter className="w-5 h-5 text-white" />
                </div>
                <h2 className="text-2xl font-bold text-gray-900 dark:text-white">{title}</h2>
              </div>

              {/* Search Form */}
              <div className="bg-neutral-100 dark:bg-neutral-500 p-6 rounded-xl mb-6 border border-gray-200 dark:border-neutral-500">
                {isGlobalSearch ? (
                  <form onSubmit={handleGlobalSearchSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Order No.</label>
                        <input
                          type="text"
                          name="orderNo"
                          value={globalSearchCriteria.orderNo}
                          onChange={handleGlobalSearchChange}
                          className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Order Ref.</label>
                        <input
                          type="text"
                          name="orderRef"
                          value={globalSearchCriteria.orderRef}
                          onChange={handleGlobalSearchChange}
                          className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Tracking No.</label>
                        <input
                          type="text"
                          name="trackingNo"
                          value={globalSearchCriteria.trackingNo}
                          onChange={handleGlobalSearchChange}
                          className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Job Type</label>
                        <input
                          type="text"
                          name="jobType"
                          value={globalSearchCriteria.jobType}
                          onChange={handleGlobalSearchChange}
                          className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Shipped By</label>
                        <select
  name="shippedBy"
  value={globalSearchCriteria.shippedBy}
  onChange={handleGlobalSearchChange}
  className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 
             border border-gray-200 dark:border-neutral-500 text-gray-900 
             dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] 
             dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
>
  <option value="">All</option>
  <option value="Royal Mail">Royal Mail</option>
  <option value="In person">In person</option>
</select>

                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Status</label>
                        <select
                          name="status"
                          value={globalSearchCriteria.status}
                          onChange={handleGlobalSearchChange}
                          className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        >
                          <option value="All">All Statuses</option>
                          <option value="Created">Created</option>
                          <option value="Received">Received</option>
                          <option value="In process">In process</option>
                          <option value="In pending / or questions">In pending / or questions</option>
                          <option value="Dispatched">Dispatched</option>

                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Start Date</label>
                        <input
                          type="date"
                          name="startDate"
                          value={globalSearchCriteria.startDate}
                          onChange={handleGlobalSearchChange}
                          className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">End Date</label>
                        <input
                          type="date"
                          name="endDate"
                          value={globalSearchCriteria.endDate}
                          onChange={handleGlobalSearchChange}
                          className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Shipped Date</label>
                        <input
                          type="date"
                          name="shippedDate"
                          value={globalSearchCriteria.shippedDate}
                          onChange={handleGlobalSearchChange}
                          className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                    </div>
                    <div className="flex space-x-4 mt-6">
                      <button
                        type="submit"
                        disabled={isSearching}
                        className="bg-green dark:bg-emerald-500 hover:bg-[#01443f] dark:hover:bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSearching ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Searching...</span>
                          </>
                        ) : (
                          <>
                            <Search className="w-4 h-4" />
                            <span>Search Orders</span>
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={clearSearch}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200"
                      >
                        Clear
                      </button>
                    </div>
                  </form>
                ) : (
                  <form onSubmit={handleUserSearchSubmit}>
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Order Ref.</label>
                        <input
                          type="text"
                          name="orderRef"
                          value={userSearchCriteria.orderRef}
                          onChange={handleUserSearchChange}
                          className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Tracking No.</label>
                        <input
                          type="text"
                          name="trackingNo"
                          value={userSearchCriteria.trackingNo}
                          onChange={handleUserSearchChange}
                          className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Job Type</label>
                        <input
                          type="text"
                          name="jobType"
                          value={userSearchCriteria.jobType}
                          onChange={handleUserSearchChange}
                          className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Shipped By</label>
                        <select
  name="shippedBy"
  value={globalSearchCriteria.shippedBy}
  onChange={handleGlobalSearchChange}
  className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 
             border border-gray-200 dark:border-neutral-500 text-gray-900 
             dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] 
             dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
>
  <option value="">All</option>
  <option value="Royal Mail">Royal Mail</option>
  <option value="In person">In person</option>
</select>

                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-600 dark:text-gray-300 mb-2">Status</label>
                        <select
                          name="status"
                          value={userSearchCriteria.status}
                          onChange={handleUserSearchChange}
                          className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        >
                          <option value="All">All Statuses</option>
                          <option value="Created">Created</option>
                          <option value="Received">Received</option>
                          <option value="In process">In process</option>
                          <option value="In pending / or questions">In pending / or questions</option>
                          <option value="Dispatched">Dispatched</option>

                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Start Date</label>
                        <input
                          type="date"
                          name="startDate"
                          value={userSearchCriteria.startDate}
                          onChange={handleUserSearchChange}
                          className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">End Date</label>
                        <input
                          type="date"
                          name="endDate"
                          value={userSearchCriteria.endDate}
                          onChange={handleUserSearchChange}
                          className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">Shipped Date</label>
                        <input
                          type="date"
                          name="shippedDate"
                          value={userSearchCriteria.shippedDate}
                          onChange={handleUserSearchChange}
                          className="w-full px-4 py-3 rounded-lg bg-white dark:bg-neutral-600 border border-gray-200 dark:border-neutral-500 text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-[#025951] dark:focus:ring-emerald-400 focus:border-transparent transition-all duration-200"
                        />
                      </div>
                    </div>
                    <div className="flex space-x-4 mt-6">
                      <button
                        type="submit"
                        disabled={isSearching}
                        className="bg-green dark:bg-emerald-500 hover:bg-[#01443f] dark:hover:bg-emerald-600 text-white px-8 py-3 rounded-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-200 flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSearching ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                            <span>Searching...</span>
                          </>
                        ) : (
                          <>
                            <Search className="w-4 h-4" />
                            <span>Search Orders</span>
                          </>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={clearSearch}
                        className="bg-gray-500 hover:bg-gray-600 text-white px-6 py-3 rounded-lg font-semibold transition-all duration-200"
                      >
                        Clear
                      </button>
                    </div>
                  </form>
                )}
              </div>

              {/* Results */}
              <div className="bg-neutral-100 dark:bg-neutral-600 p-6 rounded-xl border border-gray-200 dark:border-neutral-500">
                {searchResults !== null ? (
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                        {searchResults.length} Results Found
                      </h3>
                      <button
                        onClick={clearSearch}
                        className="text-sm text-gray-600 hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-200 underline"
                      >
                        Clear Results
                      </button>
                    </div>

                    {searchResults.length > 0 ? (
                      <div className="space-y-4">
                        {searchResults.map((order) => (
                          <div
                            key={`search-${order.id}`}
                            className={`bg-white dark:bg-neutral-600 p-6 rounded-xl border border-gray-200 dark:border-neutral-500 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer ${(order.hasUnreadMessages) ? 'border-l-4 border-l-yellow-500' : ''}`}
                            onClick={() => {
                              navigate(`/admin/edit-order/${order.id}`);
                              markOrderAsSeen(order.id);
                            }}
                          >
                            <div className="flex flex-wrap items-center justify-between text-sm sm:text-base gap-4">
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Order Ref</p>
                                <p className="font-semibold text-gray-900 dark:text-white">{order.orderRef}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Status</p>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(order.status)}`}>
                                  {order.status}
                                </span>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Job Type</p>
                                <p className="text-gray-900 dark:text-white">{order.jobType}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Order Date</p>
                                <p className="text-gray-900 dark:text-white">{new Date(order.orderDate).toLocaleDateString()}</p>
                              </div>
                            </div>

                            {/* Punctul galben în colțul din dreapta sus pentru mesaje necitite */}
                            {(order.hasUnreadMessages) && (
                              <div className="absolute top-2 right-2">
                                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No orders found matching your search criteria</p>
                      </div>
                    )}
                  </div>
                ) : selectedUser ? (
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                      Orders for {selectedUser.name} ({selectedUser.orders.length})
                    </h3>

                    {selectedUser.orders.length > 0 ? (
                      <div className="space-y-4">
                        {selectedUser.orders.map((order) => (
                          <div
                            key={`selected-${order.id}`}
                            className={`relative bg-white dark:bg-neutral-700 p-6 rounded-xl border border-gray-200 dark:border-neutral-500 shadow-sm hover:shadow-md transition-all duration-200 cursor-pointer ${order.status === 'Created' && order.isNew ? 'border-l-4 border-l-red-500' : ''} ${(order.hasUnreadMessages) ? 'border-l-4 border-l-yellow-500' : ''}`}
                            onClick={() => {
                              navigate(`/admin/edit-order/${order.id}`);
                              markOrderAsSeen(order.id);
                            }}
                          >
                            <div className="flex flex-wrap items-center justify-between text-sm sm:text-base gap-4">
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Order Ref</p>
                                <p className="font-semibold text-gray-900 dark:text-white">{order.orderRef}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Status</p>
                                <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusBadgeColor(order.status)}`}>
                                  {order.status}
                                </span>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Job Type</p>
                                <p className="text-gray-900 dark:text-white">{order.jobType}</p>
                              </div>
                              <div>
                                <p className="text-xs text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-1">Order Date</p>
                                <p className="text-gray-900 dark:text-white">{new Date(order.orderDate).toLocaleDateString()}</p>
                              </div>
                            </div>

                            {/* Punctele de notificare în colțul din dreapta sus */}
                            <div className="absolute top-2 right-2 flex space-x-1">
                              {/* Punctul galben pentru mesaje necitite */}
                              {(order.hasUnreadMessages) && (
                                <div className="w-3 h-3 bg-yellow-500 rounded-full"></div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <Package className="w-12 h-12 mx-auto mb-3 opacity-50" />
                        <p>No orders found for this user</p>
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                    <Search className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg mb-2">Select a search option</p>
                    <p className="text-sm">Choose "Search All Orders" or select a user to search their orders</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

// Helper function pentru culorile badge-urilor de status
const getStatusBadgeColor = (status: string): string => {
  switch (status) {
    case 'Created':
      return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300';
    case 'Received':
      return 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-300';
    case 'In process':
      return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300';
    case 'In pending / or questions':
      return 'bg-orange-100 text-orange-800 dark:bg-orange-900 dark:text-orange-300';
    case 'Dispatched':
      return 'bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-300';
    default:
      return 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300';
  }
};


export default AdminDashboard;